import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Select, Button, Typography, InputNumber } from 'antd';

import './modal.scss';

import { createGiveaway, updateGiveaway } from 'src/store/giveaway';

const { Text, Paragraph } = Typography;
const { Option } = Select;

export const GiveawayModal = ({ isModalOpen, selectedGiveaway, onCancel, ticketCommands }) => {
  const { t } = useTranslation('giveaway');

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const isEditMode = !!selectedGiveaway;
  const isFinished = isEditMode && selectedGiveaway?.status === 'finished';

  const initialTicketCommandId = useMemo(() => {
    // Prefer existing giveaway command id; fallback to first available ticket command for create mode
    if (isEditMode) return selectedGiveaway?.ticketCommandId || undefined;
    return ticketCommands?.[0]?._id;
  }, [isEditMode, selectedGiveaway, ticketCommands]);

  useEffect(() => {
    if (!isModalOpen) return;

    if (!isEditMode) {
      form.resetFields();
      form.setFieldsValue({
        title: '',
        winnersCount: 1,
        ticketCommandId: initialTicketCommandId
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      title: selectedGiveaway?.title || '',
      winnersCount: selectedGiveaway?.winnersCount ?? 1,
      ticketCommandId: selectedGiveaway?.ticketCommandId || initialTicketCommandId,
      manualWinnerNickname: selectedGiveaway?.winners?.[0]?.nickname || ''
    });
  }, [form, isEditMode, isModalOpen, initialTicketCommandId, selectedGiveaway]);

  const handleSubmit = useCallback(
    (values) => {
      const payload = {
        title: values.title ?? '',
        winnersCount: values.winnersCount ?? 1,
        ticketCommandId: values.ticketCommandId
      };

      if (!isEditMode) {
        dispatch(
          createGiveaway({
            payload,
            onCancel
          })
        );

        return;
      }

      dispatch(
        updateGiveaway({
          id: selectedGiveaway?._id,
          payload: {
            giveawayId: selectedGiveaway?._id,
            ...(values.manualWinnerNickname
              ? {
                  manualWinnerNickname: String(values.manualWinnerNickname).trim()
                }
              : {}),
            ...payload
          },
          onCancel
        })
      );
    },
    [dispatch, isEditMode, onCancel, selectedGiveaway]
  );

  const footerHint = useMemo(() => {
    if (!ticketCommands?.length) {
      return t(
        'modal.no_ticket_commands',
        'No active giveaway ticket commands found. Create/enable a command with “Giveaway Ticket” enabled first.'
      );
    }
    return '';
  }, [t, ticketCommands]);

  return (
    <Modal
      title={isEditMode ? t('modal.edit_title') : t('modal.create_title')}
      open={isModalOpen}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      maskClosable={false}
      destroyOnClose
    >
      {!ticketCommands?.length && (
        <Paragraph type="warning" style={{ marginBottom: 16 }}>
          {footerHint}
        </Paragraph>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={!ticketCommands?.length || isFinished}
      >
        <Form.Item
          name="title"
          label={t('fields.title')}
          rules={[
            {
              max: 120,
              message: t('errors.title_max')
            }
          ]}
        >
          <Input placeholder={t('placeholders.title')} />
        </Form.Item>

        <Form.Item
          name="ticketCommandId"
          label={t('fields.ticket_command')}
          rules={[{ required: true, message: t('errors.ticket_command_required') }]}
        >
          <Select
            placeholder={t('placeholders.ticket_command')}
            showSearch
            optionFilterProp="children"
          >
            {ticketCommands?.map((cmd) => (
              <Option key={cmd._id} value={cmd._id}>
                {cmd.trigger}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="winnersCount"
          label={t('fields.winners')}
          rules={[
            { required: true, message: t('errors.winners_required') },
            () => ({
              validator: (_, value) => {
                const num = Number(value);
                if (!Number.isFinite(num)) {
                  return Promise.reject(new Error(t('errors.winners_number')));
                }
                if (num < 1) {
                  return Promise.reject(new Error(t('errors.winners_min')));
                }
                if (num > 50) {
                  return Promise.reject(new Error(t('errors.winners_max')));
                }
                return Promise.resolve();
              }
            })
          ]}
        >
          <InputNumber min={1} max={50} style={{ width: '100%' }} disabled />
        </Form.Item>

        {isEditMode && !isFinished && (
          <Form.Item
            name="manualWinnerNickname"
            label={t('fields.manual_winner')}
            extra={t(
              'fields.manual_winner_help',
              'If specified, giveaway will be marked as finished with this winner.'
            )}
            rules={[
              {
                max: 120,
                message: t('errors.title_max')
              }
            ]}
          >
            <Input
              placeholder={t('placeholders.manual_winner', 'Enter winner nickname (optional)')}
            />
          </Form.Item>
        )}

        {isEditMode && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">{t('modal.note_snapshot')}</Text>
          </div>
        )}

        <div className="giveaway-modal__footer">
          <Button
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            {t('actions:cancel')}
          </Button>

          <Button type="primary" htmlType="submit" disabled={!ticketCommands?.length || isFinished}>
            {isEditMode ? t('actions:save') : t('actions:create')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
